import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = (concernId = "") => ({ concern_id: concernId, age_band: "", gender: "", ordered_product_ids: [] });

export default function AdminRoutines() {
  const [rules, setRules] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = async () => {
    const [r, c, p] = await Promise.all([api.get("/admin/routines"), api.get("/admin/concerns"), api.get("/admin/products")]);
    setRules(r.data); setConcerns(c.data); setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, age_band: form.age_band || null, gender: form.gender || null };
      if (editing === "new") await api.post("/admin/routines", payload);
      else await api.patch(`/admin/routines/${editing}`, payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const toggleProduct = (pid) => {
    setForm((f) => ({
      ...f,
      ordered_product_ids: f.ordered_product_ids.includes(pid)
        ? f.ordered_product_ids.filter((x) => x !== pid)
        : [...f.ordered_product_ids, pid],
    }));
  };

  const moveProduct = (pid, dir) => {
    setForm((f) => {
      const arr = [...f.ordered_product_ids];
      const i = arr.indexOf(pid);
      if (i < 0) return f;
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, ordered_product_ids: arr };
    });
  };

  const productsForConcern = products.filter((p) => p.concern_tags?.includes(form.concern_id));

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Personalisation</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">Routine Rules</h1>
          <p className="font-sans text-sm text-brand-muted mt-2">For each concern, define the ordered routine shown to the customer.</p>
        </div>
        <Button onClick={() => { setEditing("new"); setForm(empty(concerns[0]?.id || "")); }} data-testid="routine-new-btn" className="rounded-none bg-brand-primary text-brand-bone gap-2">
          <Plus strokeWidth={1.5} className="w-4 h-4" /> Add rule
        </Button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-8 bg-white border border-brand-line p-6 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="overline">Concern</Label>
              <select value={form.concern_id} onChange={(e) => setForm({ ...form, concern_id: e.target.value, ordered_product_ids: [] })} className="mt-2 w-full h-11 border border-brand-line bg-white px-3 text-sm" required>
                {concerns.map((c) => <option key={c.id} value={c.id}>{c.labels?.en || c.slug}</option>)}
              </select>
            </div>
            <div>
              <Label className="overline">Age band (optional)</Label>
              <select value={form.age_band || ""} onChange={(e) => setForm({ ...form, age_band: e.target.value })} className="mt-2 w-full h-11 border border-brand-line bg-white px-3 text-sm">
                <option value="">Any</option>
                <option value="18-25">18-25</option>
                <option value="26-35">26-35</option>
                <option value="36+">36+</option>
              </select>
            </div>
            <div>
              <Label className="overline">Gender (optional)</Label>
              <select value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="mt-2 w-full h-11 border border-brand-line bg-white px-3 text-sm">
                <option value="">Any</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="overline">Ordered products</Label>
            <p className="text-xs text-brand-muted mt-1 mb-3">Pick products tagged with this concern, then reorder.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {productsForConcern.map((p) => {
                const sel = form.ordered_product_ids.includes(p.id);
                const idx = form.ordered_product_ids.indexOf(p.id);
                return (
                  <div key={p.id} className={`border ${sel ? "border-brand-primary bg-brand-primary/5" : "border-brand-line"} p-3 flex items-center gap-3`}>
                    <img src={p.image_url} alt="" className="w-12 h-12 object-cover bg-brand-elevated" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-brand-dark truncate">{p.names?.en}</p>
                      <p className="text-xs text-brand-muted">₹{p.price}</p>
                    </div>
                    {sel && (
                      <div className="flex flex-col gap-1">
                        <button type="button" onClick={() => moveProduct(p.id, -1)} className="text-xs text-brand-dark">▲</button>
                        <span className="text-[10px] text-brand-muted text-center">{idx + 1}</span>
                        <button type="button" onClick={() => moveProduct(p.id, 1)} className="text-xs text-brand-dark">▼</button>
                      </div>
                    )}
                    <button type="button" onClick={() => toggleProduct(p.id)} className={`text-xs px-2 py-1 ${sel ? "bg-brand-primary text-brand-bone" : "border border-brand-line"}`}>
                      {sel ? "Remove" : "Add"}
                    </button>
                  </div>
                );
              })}
              {productsForConcern.length === 0 && <p className="text-xs text-brand-muted col-span-full">No products tagged with this concern.</p>}
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-none border-brand-line">Cancel</Button>
            <Button type="submit" className="rounded-none bg-brand-primary text-brand-bone">Save</Button>
          </div>
        </form>
      )}

      <div className="mt-8 grid gap-3">
        {rules.map((r) => {
          const cn = concerns.find((x) => x.id === r.concern_id);
          return (
            <div key={r.id} className="bg-white border border-brand-line p-5 flex items-center justify-between">
              <div>
                <p className="font-serif text-xl text-brand-dark">{cn?.labels?.en || "Unknown concern"}</p>
                <p className="text-xs text-brand-muted mt-1">{r.ordered_product_ids?.length || 0} products · age: {r.age_band || "any"} · gender: {r.gender || "any"}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditing(r.id); setForm({ concern_id: r.concern_id, age_band: r.age_band || "", gender: r.gender || "", ordered_product_ids: r.ordered_product_ids || [] }); }} className="text-brand-dark hover:text-brand-primary"><Pencil strokeWidth={1.5} className="w-4 h-4" /></button>
                <button onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/admin/routines/${r.id}`); load(); } }} className="text-brand-dark hover:text-brand-primary"><Trash2 strokeWidth={1.5} className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
